import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Plus, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { useCreateSource } from '@/hooks/use-sources';
import { useIntegrations } from '@/hooks';
import { usePermissions } from '@/hooks/use-permissions';
import type { CreateSourceDto } from '@/types';

interface AddGoogleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded?: () => void;
}

export function AddGoogleDialog({ open, onOpenChange, onAdded }: AddGoogleDialogProps) {
  const [googleUrl, setGoogleUrl] = useState('');
  const [syncIntervalValue, setSyncIntervalValue] = useState<number | ''>(24);
  const [syncIntervalUnit, setSyncIntervalUnit] = useState<'minutes' | 'hours'>('hours');
  const [googleSaEmail, setGoogleSaEmail] = useState<string | null>(null);

  const createSourceMutation = useCreateSource();
  const { can } = usePermissions();
  const canEdit = can('edit', 'sources');
  const { data: integrations } = useIntegrations();
  const googleIntegration = integrations?.find((i) => i.name?.toLowerCase() === 'google' && i.isConnected);

  useEffect(() => {
    if (!open) return;
    api
      .get<{ data: { saEmail: string | null } }>('/google/config')
      .then((res) => setGoogleSaEmail(res.data.data.saEmail))
      .catch(() => {
        /* non-critical */
      });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setGoogleUrl('');
      setSyncIntervalValue(24);
      setSyncIntervalUnit('hours');
    }
  }, [open]);

  const handleAdd = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to add sources.');
      return;
    }
    const url = googleUrl.trim();
    if (!url) {
      toast.error('Please enter a Google Doc or Sheet URL');
      return;
    }
    if (!url.includes('docs.google.com')) {
      toast.error('Please enter a valid Google Docs or Sheets URL');
      return;
    }

    try {
      const email = (googleIntegration?.authMetaData as Record<string, unknown> | undefined)?.email as string | undefined;

      let sourceName = url;
      if (url.includes('/spreadsheets/d/')) {
        try {
          const res = await api.get<{ data: { title: string } }>('/google/spreadsheet-tabs', { params: { url } });
          const data = res.data.data;
          if (data?.title) sourceName = data.title;
        } catch {
          // fall back to URL as name
        }
      }

      const sourceData: CreateSourceDto = {
        name: sourceName,
        type: 'INTEGRATION',
        value: url,
        metadata: {
          googleAccount: email ?? null,
          syncIntervalMinutes:
            syncIntervalValue === '' ? 0 : syncIntervalUnit === 'hours' ? syncIntervalValue * 60 : syncIntervalValue
        }
      };

      await createSourceMutation.mutateAsync(sourceData);
      toast.success('Google source added successfully');
      onAdded?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to add Google source');
      console.error('Add Google source error:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] max-w-lg p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Add Google Docs &amp; Sheets</DialogTitle>
        </DialogHeader>

        {!googleIntegration && !googleSaEmail ? (
          <div className='h-[200px] flex flex-col items-center justify-center gap-3'>
            <img src='/assets/brand/google.svg' alt='Google' className='size-8 opacity-50' />
            <div className='text-center'>
              <p className='text-sm font-medium'>Google not connected</p>
              <p className='text-xs text-muted-foreground mt-1'>
                Connect your Google account in{' '}
                <a href='/settings/integrations' className='underline underline-offset-2'>
                  Settings → Integrations
                </a>{' '}
                to use Docs as sources.
              </p>
            </div>
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            {googleSaEmail && (
              <div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
                <Label className='text-xs text-muted-foreground'>Share your doc with the Kim bot first</Label>
                <div className='flex items-center gap-2'>
                  <code className='flex-1 bg-neutral-100 border border-neutral-200 text-neutral-800 px-2 py-1.5 rounded-md overflow-auto text-xs font-mono'>
                    {googleSaEmail}
                  </code>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='text-xs'
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(googleSaEmail);
                        toast.success('Copied to clipboard');
                      } catch (e) {
                        console.warn('Clipboard write failed', e);
                      }
                    }}
                  >
                    <Copy className='size-3.5' /> Copy
                  </Button>
                </div>
                <p className='text-xs text-muted-foreground'>
                  Give it <span className='font-medium text-foreground'>Viewer</span> access, then paste the URL below.
                </p>
              </div>
            )}
            {(() => {
              const isSheet = googleUrl.includes('/spreadsheets/d/');
              return (
                <div className='flex flex-col gap-2'>
                  <Input
                    type='text'
                    value={googleUrl}
                    onChange={(e) => setGoogleUrl(e.target.value)}
                    placeholder='https://docs.google.com/document/d/...'
                    className='text-sm'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAdd();
                    }}
                  />
                  {isSheet ? (
                    <p className='text-xs text-muted-foreground'>
                      Google Sheets are shown as a read-only preview and aren&apos;t used to answer questions (used via
                      workflow tools instead).
                    </p>
                  ) : (
                    <>
                      <p className='text-xs text-muted-foreground'>Paste a Google Docs URL to index it as a knowledge base source.</p>
                      <div className='flex items-center gap-2'>
                        <span className='text-xs text-muted-foreground'>Auto-sync every</span>
                        <Input
                          type='number'
                          min={0}
                          value={syncIntervalValue}
                          placeholder='0'
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              setSyncIntervalValue('');
                            } else {
                              const val = parseInt(raw, 10);
                              if (!isNaN(val) && val >= 0) setSyncIntervalValue(val);
                            }
                          }}
                          className='h-7 w-20 text-xs px-2'
                        />
                        <select
                          value={syncIntervalUnit}
                          onChange={(e) => setSyncIntervalUnit(e.target.value as 'minutes' | 'hours')}
                          className='h-7 text-xs border border-input rounded-md px-1.5 bg-background'
                        >
                          <option value='minutes'>minutes</option>
                          <option value='hours'>hours</option>
                        </select>
                        <span className='text-xs text-muted-foreground'>(0 = no auto-sync)</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        <div className='flex gap-2 justify-end mt-2'>
          <Button type='button' variant='outline' className='text-xs' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {(googleIntegration || googleSaEmail) && (
            <Button
              type='button'
              className='text-xs'
              onClick={handleAdd}
              disabled={!googleUrl.trim() || createSourceMutation.isPending || !canEdit}
            >
              <Plus className='size-4' /> Add
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
